import type {
  IconProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import { Icon, calc, motion, transition } from '@hover/blueprint';
import { resetFocus } from '@hover/blueprint/foundation';
import {
  iArrowLeft,
  iArrowRight,
  iChevronLeft,
  iChevronRight,
} from '@hover/icons';
import React, { forwardRef } from 'react';

export interface ArrowButtonProps
  extends MotionPropsFor<IntrinsicSystemProps<'button'>> {
  arrowType?: 'chevron' | 'arrow';
  direction: 'start' | 'end';
  disabledColor?: IconProps['color'];
  focusColor?: IconProps['color'];
  isDisabled?: boolean;
  size?: IconProps['size'];
  strokeWidth?: IconProps['strokeWidth'];
}

const ArrowButton = forwardRef<HTMLButtonElement, ArrowButtonProps>(
  (
    {
      arrowType,
      direction,
      disabledColor = 'darkAlpha.300',
      focusColor = 'darkAlpha.300',
      isDisabled = false,
      size = 'medium',
      strokeWidth,
      ...props
    },
    ref,
  ) => {
    const icon = () => {
      switch (arrowType) {
        case 'arrow':
          return direction === 'start' ? iArrowLeft : iArrowRight;
        default:
          return direction === 'start' ? iChevronLeft : iChevronRight;
      }
    };

    return (
      <motion.button
        _disabled={{ color: disabledColor, cursor: 'unset' }}
        _focusVisible={{ outline: 'none' }}
        alignItems="center"
        bg="transparent"
        border="none"
        bottom={0}
        color="neutral.900"
        cursor="pointer"
        data-group
        disabled={isDisabled}
        display={{ base: 'none', tablet: 'flex' }}
        justifyContent="center"
        p="0"
        sx={resetFocus()}
        top={0}
        {...transition.easing250('color')}
        {...props}
        ref={ref}
      >
        <Icon
          _groupFocusVisible={{ color: focusColor }}
          color="transparent"
          icon={icon()}
          m={0}
          position="absolute"
          size={size}
          strokeWidth={strokeWidth ? calc.add(strokeWidth, '4.5px') : '6px'}
          {...transition.easing250('color')}
        />
        <Icon
          className="icon"
          color="inherit"
          icon={icon()}
          m={0}
          size={size}
          strokeWidth={strokeWidth}
        />
      </motion.button>
    );
  },
);

export { ArrowButton };
